/**
 * Comprensión determinista (sin IA, sin red).
 *
 * No es una lista de frases: el mensaje se normaliza, se extraen rasgos (fechas, horas,
 * franjas, duraciones, días, hábitos mencionados, si es una pregunta) y cada intención
 * suma puntos con un léxico ponderado de raíces. La mejor gana solo si supera un umbral y
 * se distancia de la segunda; si no, se devuelve `unknown` con las candidatas para que la
 * conversación pregunte. Las órdenes puntuales (crear, registrar, listas, notas…) siguen
 * resueltas por el intérprete de órdenes existente (deterministic.ts).
 *
 * Límite honesto: entiende variaciones de vocabulario y orden, pero no paráfrasis libres
 * ni frases sin ninguna palabra clave reconocible.
 */
import type { LocalDate } from '@habitos/engine';
import { interpretDeterministic, matchHabits } from './deterministic';
import { parsePreference } from './preferences';
import type { Changes, Intent, Understander, Understanding, UnderstandContext, Window } from './intents';
import { ALL_DAYS, Phrase, simplify, takeDate, takeDaySet, takeDuration, takeFreeDays, takeRange, takeTime, takeWeek, takeWeeklyAmount, WEEKDAY, weekdayNumber } from './normalize';

const u = (intent: Intent, confidence: number): Understanding => ({ intent, confidence });
const unknown = (candidates: string[] = []): Understanding => u({ type: 'unknown', candidates }, 0);

// --- Palabras de control ----------------------------------------------------------

const CONFIRM_STRONG = new Set(['si', 'vale', 'dale', 'ok', 'okay', 'okey', 'perfecto', 'confirmo', 'confirmar', 'confirma', 'adelante', 'hazlo', 'guardalo', 'guardala', 'guarda', 'guardar', 'listo', 'correcto', 'exacto', 'claro', 'venga', 'acuerdo', 'eliminar', 'eliminalo', 'elimina', 'archivar', 'archivalo', 'archiva', 'borrar', 'borralo', 'aplicar', 'aplica', 'aplicalo']);
const CONFIRM_FILLER = new Set(['por', 'favor', 'gracias', 'de', 'eso', 'es', 'me', 'parece', 'bien', 'supuesto', 'todo', 'asi', 'muy', 'genial', 'lo', 'la', 'esta']);
const DESTRUCTIVE_WORDS = new Set(['eliminar', 'eliminalo', 'elimina', 'archivar', 'archivalo', 'archiva', 'borrar', 'borralo']);
const CANCEL_STRONG = new Set(['no', 'cancela', 'cancelar', 'cancelalo', 'cancelala', 'olvidalo', 'olvidala', 'olvida', 'dejalo', 'dejala', 'descarta', 'descartalo', 'descartala', 'nada', 'basta', 'para']);
const CANCEL_FILLER = new Set(['mejor', 'gracias', 'por', 'favor', 'al', 'final', 'eso', 'lo', 'la', 'todo', 'ya', 'mas', 'tranquilo', 'no']);

function onlyFrom(tokens: string[], strong: Set<string>, filler: Set<string>): boolean {
  return tokens.length > 0 && tokens.length <= 6 && tokens.some((t) => strong.has(t)) && tokens.every((t) => strong.has(t) || filler.has(t));
}

// --- Rasgos ---------------------------------------------------------------------------

/** Cambios concretos en el mensaje. Consume lo que reconoce de `p`. */
export function extractChanges(p: Phrase, today: LocalDate): Changes {
  const c: Changes = {};
  const named = p.take(/\b(?:llamalo|llamala|ponle (?:de |por )?nombre|que se llame|renombra(?:lo|la)?(?: a| como)?|cambia(?:le)? el nombre (?:a|por))\s+(.+)$/);
  if (named) c.name = capitalize(p.raw.slice(named.index + named[0].length - named[1]!.length, named.index + named[0].length).trim()).slice(0, 80);
  if (p.take(/\bsin (?:hora|horario)(?: fij[oa])?\b/)) c.time = null;
  if (p.has(/\b(?:tambien|ademas)\b/)) c.addDays = true;
  const free = takeFreeDays(p);
  if (free) c.freeDays = free;
  const weekly = takeWeeklyAmount(p);
  if (weekly) c.weeklyMinutes = weekly;
  const days = takeDaySet(p);
  if (days?.type === 'days' && days.days.length) {
    c.days = days.days;
    if (days.except.length) c.except = days.except;
  }
  if (days?.type === 'quota') c.quota = days.times;
  const minutes = takeDuration(p);
  if (minutes !== null && minutes >= 1 && minutes <= 600) c.minutes = minutes;
  const time = takeTime(p);
  if (time.type === 'time') c.time = time.time;
  else if (time.type === 'ambiguous') c.timeOptions = time.options;
  else if (time.type === 'slot') c.slot = time.slot;
  else if (time.type === 'invalid') c.invalidTime = true;
  const date = takeDate(p, today);
  if (date.type === 'date') c.date = date.date;
  else if (date.type === 'invalid') c.invalidDate = true;
  if (c.time === undefined && !c.timeOptions && !c.slot && p.has(/\b(?:horario|hora|otra hora)\b/)) c.askTime = true;
  return c;
}

export function hasChanges(c: Changes): boolean {
  return Object.keys(c).some((k) => k !== 'addDays');
}

const capitalize = (s: string) => (s ? s[0]!.toLocaleUpperCase() + s.slice(1) : s);

/**
 * Disponibilidad: "entre semana de 18 a 20 y el sábado por la mañana". Cada tramo horario se
 * asocia a los días mencionados antes; un tramo sin días hereda los del anterior, y si no hay
 * ninguno se aplica a toda la semana (se explica en la propuesta).
 */
export function parseAvailability(text: string): { windows: Window[]; ambiguous: boolean; assumedAllDays: boolean; daysWithoutRange: number[] } {
  const n = simplify(text);
  const boundary = /\s*(?:,|;|\s(?:y|e|pero|ademas|tambien)\s)\s*(?=(?:el |los |la |cada )?(?:entre semana|fines? de semana|finde|lunes|martes|miercoles|jueves|viernes|sabados?|domingos?|de \d|desde|por la|de la|entre las? \d|de (?:una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce)\b|todos los dias|a diario))/;
  const segments = n.split(boundary).filter(Boolean);
  const windows: Window[] = [];
  let carried: number[] | null = null;
  let waiting: number[] = [];
  let ambiguous = false;
  let assumedAllDays = false;
  for (const seg of segments) {
    const p = new Phrase(seg);
    const range = takeRange(p);
    const ds = takeDaySet(p);
    const segDays = ds?.type === 'days' ? ds.days : [];
    if (range && 'ambiguous' in range) {
      ambiguous = true;
      continue;
    }
    if (!range) {
      waiting = [...new Set([...waiting, ...segDays])];
      continue;
    }
    let days = [...new Set([...waiting, ...segDays])].sort((a, b) => a - b);
    if (!days.length) days = carried ?? [];
    if (!days.length) {
      days = ALL_DAYS;
      assumedAllDays = true;
    }
    windows.push({ days, start: range.start, end: range.end });
    carried = days;
    waiting = [];
  }
  return { windows, ambiguous, assumedAllDays, daysWithoutRange: waiting };
}

// --- Léxico ponderado -------------------------------------------------------------------

type Scorable = 'query_stats' | 'query_habits' | 'query_tasks' | 'query_agenda' | 'query_focus' | 'query_notes' | 'query_journal' | 'plan_week' | 'plan_goal' | 'edit_habit' | 'reschedule_task' | 'log_focus';

const ROUTINE_ADD = /^(?:agrega|agregale|agregar|anade|anadir|suma|sumale|sumar|mete|meter|incluye|incluir|incorpora|incorporar|pon|ponme)\b.*\b(?:a|en) (?:mi|la) (?:rutina|semana|dia a dia|dia)\b/;
const UNDO_LAST = /^(?:(?:por favor )?(?:deshace|deshacer|deshaz|revierte|revertir|anula|anular|cancela|cancelar|quita|quitar|elimina|eliminar|borra|borrar)(?:lo|la)? (?:el |lo |la )?(?:ultimo|ultima|anterior)(?: (?:cambio|accion|que hiciste|que guardaste|paso))?|deshacer|deshaz|deshazlo|vuelve atras|volve atras|vuelve como estaba|volve como estaba)$/;
const ALTERNATIVE = /\b(?:otra|otras) (?:alternativa|alternativas|opcion|opciones|propuesta|forma|manera|version|distribucion)\b|^(?:muestrame|mostrame|dame|proponme|ensename|quiero|hay) otra\b|^alternativa\b|\bde otra manera\b/;
const MOVE_DAY = new RegExp(`\\b(?:pasa|pasar|pasalo|pasala|pasemos|mueve|mover|muevelo|muevela|movamos|cambia|cambiar|cambiemos|traslada|trasladar|corre|correr)\\b(.*?)\\b(?:del?|el) ${WEEKDAY} (?:al?|para el|hacia el) ${WEEKDAY}\\b`);
const ROUTINE = /\b(?:organiz|planific|plane[ae]|arm|haz|hace|prepar|distribu|ordena|cuadra)\w*\b.*?\b(?:semana|rutina|dias|horario|algo|plan|agenda)\b.*?\bpara\s+(.+(?:,|\sy\s|\se\s).+)$/;
/** Actividades que significan descanso: se traducen en días libres, no en hábitos. */
const REST = /^(?:descansar|descanso|relajarme|relajar|desconectar|no hacer nada|tener tiempo libre|tiempo libre)\b/;

const LEXICON: Record<Scorable, [RegExp, number][]> = {
  query_stats: [
    [/\b(?:descuid|abandon|olvidando|fallando|flojo|floja|me cuesta|dejando de lado|peor(?:es)?\b|desatend)/, 3.5],
    [/\b(?:estadistic|progreso|racha|como voy|que tal voy|como llevo|cumpl(?!eano)|resumen|rendimiento|porcentaje|constancia|mejor(?:es)? habito)/, 2.5],
    [/\b(?:ultimamente|ultim[oa]s? (?:dias|semanas)|este mes)\b/, 0.5],
    [/\bhabitos?\b/, 0.5],
  ],
  query_habits: [
    [/\b(?:que|cuales|mis|ver|lista de|todos mis) habitos\b|\bhabitos (?:tengo|activos)\b/, 2.5],
    [/\bhabitos\b/, 0.5],
  ],
  query_tasks: [
    [/\btareas?\b.*\bpendientes?\b|\bque tareas\b|\bmis tareas\b|\btareas (?:tengo|hay)\b|\bpendientes\b/, 2.5],
    [/\btareas?\b/, 0.5],
  ],
  query_agenda: [
    [/\bagenda\b|\bque tengo\b|\bque hay (?:para|el|hoy|manana)\b|\bque me toca\b|\bcomo tengo\b|\bque tengo previsto\b/, 2.5],
    [/\b(?:hoy|manana|semana|lunes|martes|miercoles|jueves|viernes|sabado|domingo)\b/, 0.5],
    // "Mis pendientes de hoy" es la agenda del día (tareas y hábitos), no solo las tareas.
    [/\bpendientes (?:de|para) (?:hoy|manana)\b/, 3],
  ],
  query_focus: [
    [/\b(?:enfoque|concentracion|concentrado|pomodoros?)\b/, 1.5],
    [/\b(?:cuanto|cuantas|cuantos|tiempo|minutos|sesiones|horas)\b/, 1],
  ],
  query_notes: [
    [/\b(?:busca|buscar|encuentra|encuentrame)\b.*\b(?:notas?|ideas?|cerebro)\b|\bnotas? (?:sobre|acerca de|con)\b|\bque (?:anote|apunte|guarde)\b|\b(?:mis|ver|las) (?:notas|ideas)\b/, 3],
  ],
  query_journal: [
    [/\bdiario\b/, 1.5],
    // Secundario: "ver" o "dime" sin "diario" no apuntan al diario.
    [/\b(?:que escribi|que puse|que anote|leer|muestra|ensena|ver|dime)\b/, 1.4],
  ],
  plan_week: [
    [/\b(?:organiz|planific|plane[ae]|ordena|distribu|cuadra)\w*/, 2],
    [/\bsemana\b/, 1.5],
    [/\b(?:ayudame|ayuda) (?:a|con)\b/, 0.5],
  ],
  plan_goal: [
    [/^(?:yo )?(?:quiero|quisiera|me gustaria|voy a|necesito|me propongo|deberia|intentare|planeo|mi objetivo es|mi meta es)\b/, 1.5],
    // "Agrega 30 minutos de lectura a mi rutina".
    [ROUTINE_ADD, 3],
  ],
  edit_habit: [
    [/\b(?:cambia|cambiar|cambiemos|cambialo|modifica|modificar|ajusta|ajustar|pon|ponle|ponme|mueve|actualiza|sube|baja)\w*/, 1.5],
    [/\b(?:recordatorio|aviso|alarma|horario|meta|objetivo|nombre|frecuencia)\b/, 1.5],
  ],
  reschedule_task: [
    [/\b(?:pasa|pasala|pasalo|mueve|muevela|aplaza|aplazala|pospon|posponla|reprograma|cambia|deja|dejala)\w*/, 1.5],
    [/\btarea\b/, 1.5],
  ],
  log_focus: [
    [/^(?:registra|registrar|anota|anotar|apunta|apuntar|guarda|suma|añade|anade)\w*/, 1.5],
    [/\b(?:enfoque|concentracion|pomodoro|sesion)\b/, 1.5],
  ],
};

const PRIMARY = 1.5;
const QUESTION_START = /^(?:que|cual|cuales|cuanto|cuanta|cuantos|cuantas|como|cuando|donde|dime|decime|muestrame|mostrame|muestra|ensename|contame|quiero ver|hay|tengo)\b/;
const QUERY_TYPES = new Set<Scorable>(['query_stats', 'query_habits', 'query_tasks', 'query_agenda', 'query_focus', 'query_notes', 'query_journal']);

function scoreAll(n: string, isQuestion: boolean, features: { duration: boolean; days: boolean; habitMatch: boolean; date: boolean; activity: boolean }): [Scorable, number][] {
  const out: [Scorable, number][] = [];
  for (const [type, rules] of Object.entries(LEXICON) as [Scorable, [RegExp, number][]][]) {
    // Hace falta un indicio principal (peso >= PRIMARY): las palabras genéricas ("tiempo",
    // "mañana", "semana") no bastan por sí solas para proponer esa intención.
    if (!rules.some(([re, w]) => w >= PRIMARY && re.test(n))) continue;
    let s = 0;
    for (const [re, w] of rules) if (re.test(n)) s += w;
    if (QUERY_TYPES.has(type) && isQuestion) s += 1;
    if (type === 'plan_goal') {
      if (features.activity) s += 1;
      if (features.duration) s += 1;
      if (features.days) s += 1;
    }
    if (type === 'edit_habit' && features.habitMatch) s += 1;
    if (type === 'reschedule_task' && features.date) s += 0.5;
    if (type === 'log_focus' && features.duration) s += 0.5;
    // Una pregunta no es una petición de cambio.
    if ((type === 'edit_habit' || type === 'reschedule_task' || type === 'log_focus') && isQuestion && !/^(?:puedes|podrias)\b/.test(n)) s -= 1.5;
    out.push([type, s]);
  }
  return out.sort((a, b) => b[1] - a[1]);
}

/** Actividad de un objetivo: lo que queda al quitar el verbo inicial, la duración, los días y la hora. */
function goalActivity(input: string, today: LocalDate): { activity: string | null; changes: Changes } {
  const p = new Phrase(input);
  p.take(/^\s*(?:yo )?(?:quiero|quisiera|me gustaria|voy a|necesito|me propongo|deberia|intentare|planeo|mi objetivo es|mi meta es)\s+(?:empezar a |comenzar a |intentar |poder |volver a |ponerme a )?/);
  p.take(/^\s*(?:agrega|agregale|agregar|anade|anadir|suma|sumale|sumar|mete|meter|incluye|incluir|incorpora|incorporar|pon|ponme)\s+/);
  // "a mi rutina", "a la rutina", "a mi semana"; nunca "a la semana" (es una cantidad semanal).
  p.take(/\s*\b(?:a|en) (?:mi (?:rutina|semana|dia a dia|dia)|la rutina)\b/);
  const changes = extractChanges(p, today);
  let rest = p.rest().replace(/\s+(?:y|e|,)$/i, '').replace(/^(?:a|de|del|que|un|una|unos|unas)\s+/i, '').replace(/^(?:de|del)\s+/i, '');
  rest = rest.replace(/\s+(?:y|e|,)$/i, '').trim();
  return { activity: rest ? capitalize(rest).slice(0, 80) : null, changes };
}

function queryNotesText(input: string): string {
  const p = new Phrase(input);
  p.take(/\b(?:busca(?:r|me)?|encuentra(?:me)?|muestrame|ensename|ver|dime)\b/);
  p.take(/\b(?:en )?(?:mis |las )?(?:notas?|ideas?|cerebro)\b/);
  p.take(/\b(?:que (?:anote|apunte|guarde))\b/);
  p.take(/^\s*(?:sobre|acerca de|con|de|que hablen de|que digan)\s+/);
  return p.rest().replace(/^(?:sobre|acerca de|con|de|el|la|los|las)\s+/i, '').slice(0, 80);
}

// --- Entrada principal ---------------------------------------------------------------------

export function understandDeterministic(text: string, ctx: UnderstandContext): Understanding {
  const input = text.trim().replace(/\s+/g, ' ').slice(0, 500);
  const n = simplify(input);
  if (!n) return unknown();
  const tokens = n.split(' ');
  const today = ctx.today as LocalDate;

  if (onlyFrom(tokens, CONFIRM_STRONG, CONFIRM_FILLER) && !(tokens.length === 1 && tokens[0] === 'para')) {
    if (ctx.pending) return u({ type: 'confirm', explicit: tokens.some((t) => DESTRUCTIVE_WORDS.has(t)) }, 0.95);
  }
  if (onlyFrom(tokens, CANCEL_STRONG, CANCEL_FILLER)) return u({ type: 'cancel' }, ctx.pending ? 0.95 : 0.8);
  if (/^(?:empecemos de nuevo|empezar de nuevo|empieza de nuevo|nueva conversacion|borra (?:la |esta )?conversacion|reinicia(?:r)?(?: la conversacion)?)$/.test(n)) return u({ type: 'reset' }, 0.95);
  if (/^(?:hola|buenas|buenos dias|buenas tardes|buenas noches|hey|que tal)\b/.test(n) && tokens.length <= 4) return u({ type: 'greeting' }, 0.9);
  if (/^(?:gracias|muchas gracias|mil gracias|genial gracias|perfecto gracias)$/.test(n)) return u({ type: 'thanks' }, 0.9);
  if (/^(?:ayuda|que puedes hacer|que sabes hacer|como funciona(?:s)?|que haces|para que sirves|help)$/.test(n)) return u({ type: 'help' }, 0.95);

  if (UNDO_LAST.test(n)) return u({ type: 'undo_last' }, 0.9);
  if (/^(?:que sabes de mi|que recuerdas de mi|que preferencias tengo|mis preferencias|que has aprendido de mi)$/.test(n)) return u({ type: 'query_prefs' }, 0.95);
  const pref = parsePreference(input);
  if (pref) return u({ type: 'remember_pref', change: pref }, 0.9);
  if (ALTERNATIVE.test(n) && tokens.length <= 8) return u({ type: 'alternative' }, 0.9);
  const move = MOVE_DAY.exec(n);
  if (move && move[1]!.trim()) return u({ type: 'move_day', targetText: input, from: weekdayNumber(move[2]!), to: weekdayNumber(move[3]!) }, 0.85);
  const routine = ROUTINE.exec(n);
  if (routine) {
    const r = routineIntent(input, routine[1]!, ctx);
    if (r) return u(r, 0.85);
  }

  const isQuestion = /[?¿]/.test(input) || QUESTION_START.test(n);
  const habits = ctx.habits.filter((h) => !h.archived);
  const habitMatch = matchHabits(input, habits).length > 0;
  const probe = new Phrase(input);
  const changes = extractChanges(probe, today);
  const goal = goalActivity(input, today);
  const scored = scoreAll(n, isQuestion, {
    duration: changes.minutes !== undefined || changes.weeklyMinutes !== undefined,
    days: Boolean(changes.days || changes.quota || changes.weeklyMinutes),
    habitMatch,
    date: Boolean(changes.date),
    activity: Boolean(goal.activity),
  });
  const [best, second] = scored;

  // "Déjame libre el domingo" sin nada en curso: mover lo que haya ese día.
  if (changes.freeDays && !ctx.pending && !best?.[0].startsWith('plan_')) return u({ type: 'free_day', days: changes.freeDays }, 0.85);

  // Con una propuesta abierta, un mensaje que solo trae cambios la modifica.
  const editWithoutTarget = best && (best[0] === 'edit_habit' || best[0] === 'reschedule_task') && !habitMatch;
  if (ctx.pending && (!best || best[1] < 3 || editWithoutTarget)) {
    if (/^(?:quita|quitar|saca|sacar|elimina|borra|descarta|sin)\b/.test(n) && !changes.days) return u({ type: 'remove_item', text: input }, 0.8);
    if (hasChanges(changes)) return u({ type: 'modify', changes, text: input }, 0.85);
  }

  if (best && best[1] >= 2) {
    if (second && second[1] >= 2 && best[1] - second[1] < 0.75) return unknown([best[0], second[0]]);
    const intent = intentFor(best[0], input, ctx);
    if (intent) {
      // 2–3 puntos: interpretación probable pero no segura; la conversación la confirma antes de seguir.
      const confidence = Math.min(0.95, best[1] / 5);
      if (best[1] >= 3 || confidence >= 0.5) return u(intent, best[1] >= 3 ? Math.max(confidence, 0.6) : confidence);
    }
  }

  const legacy = interpretDeterministic(input, ctx);
  if (legacy.type === 'proposal' && legacy.proposal.kind === 'show_today') return u({ type: 'query_agenda', from: today, to: today }, 0.9);
  if (legacy.type !== 'unsupported') return u({ type: 'command', interpretation: legacy }, 0.9);

  if (ctx.pending && hasChanges(changes)) return u({ type: 'modify', changes, text: input }, 0.7);
  return unknown(scored.filter(([, s]) => s >= 1).slice(0, 3).map(([t]) => t));
}

/**
 * "Organízame la semana para estudiar 6 horas, entrenar tres veces y descansar el domingo":
 * cada actividad con sus propios cambios; las de descanso se convierten en días libres.
 */
function routineIntent(input: string, list: string, ctx: UnderstandContext): Intent | null {
  const today = ctx.today as LocalDate;
  // Se trabaja sobre el texto original a partir de "para" para conservar mayúsculas y tildes.
  const at = input.toLocaleLowerCase().lastIndexOf(' para ');
  const raw = at >= 0 ? input.slice(at + 6) : list;
  // Se separa por comas e "y", salvo dentro de una lista de días ("los lunes y jueves").
  const notDay = '(?!\\s*(?:los |el |la )?(?:lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bados?|domingos?|fines? de semana)\\b)';
  const segments = raw
    .split(new RegExp(`\\s*(?:,${notDay}|;|\\s(?:y|e)\\s${notDay})\\s*`, 'i'))
    .map((x) => x.trim())
    .filter(Boolean);
  const activities: { name: string; changes: Changes }[] = [];
  const freeDays = new Set<number>();
  let rest = false;
  for (const seg of segments.slice(0, 6)) {
    const p = new Phrase(seg);
    const c = extractChanges(p, today);
    const name = p.rest().replace(/^(?:a|de|que|para)\s+/i, '').trim();
    if (c.freeDays) c.freeDays.forEach((d) => freeDays.add(d));
    if (REST.test(simplify(seg))) {
      rest = true;
      c.days?.forEach((d) => freeDays.add(d));
      continue;
    }
    if (name) activities.push({ name: capitalize(name).slice(0, 80), changes: c });
  }
  if (!activities.length) return null;
  // "Descansar" sin día: el domingo, explicado en la propuesta y fácil de cambiar.
  if (rest && !freeDays.size) freeDays.add(7);
  const av = parseAvailability(input);
  return { type: 'plan_routine', activities, freeDays: [...freeDays].sort((a, b) => a - b), windows: av.windows.length ? av.windows : null };
}

/** Intención de un tipo para este texto (también la usa la conversación al elegir una candidata). */
export function intentFor(type: string, input: string, ctx: UnderstandContext): Intent | null {
  const today = ctx.today as LocalDate;
  const n = simplify(input);
  const changes = (): Changes => extractChanges(new Phrase(input), today);
  switch (type) {
    case 'query_stats': {
      const neglected = /\b(?:descuid|abandon|olvidando|fallando|flojo|floja|me cuesta|dejando de lado|peor|desatend)/.test(n);
      const habit = matchHabits(input, ctx.habits.filter((h) => h.kind !== 'task')).length > 0;
      return { type: 'query_stats', scope: neglected ? 'neglected' : habit ? 'habit' : 'summary', habitText: habit ? input : null };
    }
    case 'query_habits':
      return { type: 'query_habits' };
    case 'query_tasks':
      return { type: 'query_tasks' };
    case 'query_agenda': {
      const p = new Phrase(input);
      const week = takeWeek(p, today, ctx.weekStart);
      if (week) return { type: 'query_agenda', from: week.from, to: week.to };
      const d = takeDate(p, today, true);
      const day = d.type === 'date' ? d.date : today;
      return { type: 'query_agenda', from: day, to: day };
    }
    case 'query_focus':
      return { type: 'query_focus' };
    case 'query_notes':
      return { type: 'query_notes', query: queryNotesText(input) };
    case 'query_journal': {
      const d = takeDate(new Phrase(input), today, true);
      return { type: 'query_journal', date: d.type === 'date' ? d.date : null };
    }
    case 'plan_week': {
      const av = parseAvailability(input);
      return { type: 'plan_week', windows: av.windows.length ? av.windows : null };
    }
    case 'plan_goal': {
      const goal = goalActivity(input, today);
      const c = goal.changes;
      // "A mi rutina" sin frecuencia: a diario (se explica y se puede cambiar).
      if (ROUTINE_ADD.test(n) && !c.days && !c.quota && !c.weeklyMinutes) c.days = ALL_DAYS;
      return { type: 'plan_goal', activity: goal.activity, changes: c };
    }
    case 'edit_habit':
      return { type: 'edit_habit', habitText: input, changes: changes() };
    case 'reschedule_task':
      return { type: 'reschedule_task', taskText: input, changes: changes() };
    case 'log_focus': {
      const p = new Phrase(input);
      const minutes = takeDuration(p);
      const d = takeDate(p, today, true);
      return { type: 'log_focus', minutes: minutes && minutes <= 600 ? minutes : null, habitText: input, date: d.type === 'date' ? d.date : null };
    }
    default:
      return null;
  }
}

export const deterministicUnderstander: Understander = {
  id: 'deterministic',
  async understand(text, ctx) {
    return understandDeterministic(text, ctx);
  },
};
